import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { setAuthState, resetRedirectGuard, setAuthToken as setAuthTokenStore, getCsrfToken } from '@/lib/apiClient';

const API = (window.location.origin || '') + '/api';

const AuthContext = createContext(null);

/**
 * Auth tokens are managed via httpOnly cookies (set by the backend) for XSS safety.
 * Token is also kept in React memory state for backwards compatibility with
 * components that still build "Authorization: Bearer ..." headers manually.
 * The token is NEVER persisted to localStorage.
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setTokenState] = useState(null);

  // Keep apiClient's in-memory token store in sync so legacy admin components
  // can read it via getAuthToken() without touching localStorage.
  const setToken = useCallback((t) => {
    setTokenState(t);
    setAuthTokenStore(t);
  }, []);

  // Request deduplication guard
  const authCheckInProgress = useRef(false);
  const lastAuthCheck = useRef(0);
  const AUTH_CHECK_DEBOUNCE = 2000; // Minimum 2s between auth checks

  const checkAuth = useCallback(async (force = false) => {
    if (authCheckInProgress.current) {
      return user !== null;
    }

    const now = Date.now();
    if (!force && now - lastAuthCheck.current < AUTH_CHECK_DEBOUNCE) {
      return user !== null;
    }

    authCheckInProgress.current = true;
    lastAuthCheck.current = now;

    try {
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = 'Bearer ' + token;

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);

      const response = await window.fetch(API + '/auth/me', {
        method: 'GET',
        headers: headers,
        credentials: 'include',
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        setAuthState(true);
        return true;
      }

      if (response.status === 401) {
        setUser(null);
        setToken(null);
        setAuthState(false);
      }
      return false;
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('[Auth] Check failed:', error.message);
      }
      return user !== null;
    } finally {
      setLoading(false);
      authCheckInProgress.current = false;
    }
  }, [token, user]);

  const login = useCallback(async (email, password) => {
    try {
      const response = await window.fetch(API + '/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email, password: password }),
        credentials: 'include'
      });

      let data;
      try {
        data = await response.json();
      } catch (e) {
        throw new Error('Server error - please try again');
      }

      if (!response.ok) {
        throw new Error(data.detail || 'Login failed');
      }

      setUser(data.user);
      setToken(data.token);
      setAuthState(true);
      resetRedirectGuard();
      return data.user;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }, []);

  const register = useCallback(async (email, password, name, selectedTrack) => {
    const response = await window.fetch(API + '/auth/register', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email, password: password, name: name, selected_track: selectedTrack }),
      credentials: 'include'
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.detail || 'Registration failed');
    }

    setUser(data.user);
    setToken(data.token);
    return data.user;
  }, []);

  const continueAsGuest = useCallback(async (selectedTrack) => {
    const track = selectedTrack || 'PMP';

    try {
      const response = await window.fetch(API + '/auth/guest', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ selected_track: track }),
        credentials: 'include'
      });

      let data;
      try {
        data = await response.json();
      } catch (e) {
        throw new Error('Server error - please try again');
      }

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to create guest session');
      }

      setUser(data.user);
      setToken(data.token);
      return data.user;
    } catch (error) {
      console.error('Guest session error:', error);
      throw error;
    }
  }, []);

  const processOAuthSession = useCallback(async (sessionId) => {
    const pendingTrack = localStorage.getItem('pendingTrack') || 'PMP';
    localStorage.removeItem('pendingTrack');

    const response = await window.fetch(API + '/auth/session', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ session_id: sessionId, selected_track: pendingTrack }),
      credentials: 'include'
    });

    if (!response.ok) {
      throw new Error('OAuth session processing failed');
    }

    const data = await response.json();
    // Backend returns {token, user}
    if (data && data.user) {
      setUser(data.user);
      if (data.token) setToken(data.token);
      return data.user;
    }
    setUser(data);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = 'Bearer ' + token;

      await window.fetch(API + '/auth/logout', {
        method: 'POST',
        headers: headers,
        credentials: 'include'
      });
    } catch (error) {
      console.error('Logout error:', error);
    }

    setUser(null);
    setToken(null);
  }, [token]);

  const getAuthHeaders = useCallback(() => {
    const headers = {};
    if (token) headers['Authorization'] = 'Bearer ' + token;
    const csrf = getCsrfToken();
    if (csrf) headers['X-CSRF-Token'] = csrf;
    return headers;
  }, [token]);

  const requestPasswordReset = useCallback(async (email) => {
    const response = await window.fetch(API + '/auth/request-password-reset', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email })
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.detail || 'Failed to request password reset');
    }

    return data;
  }, []);

  const resetPassword = useCallback(async (resetToken, newPassword) => {
    const response = await window.fetch(API + '/auth/reset-password', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: resetToken, new_password: newPassword })
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.detail || 'Failed to reset password');
    }

    return data;
  }, []);

  const verifyEmail = useCallback(async (verificationToken) => {
    const response = await window.fetch(API + '/auth/verify-email', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: verificationToken })
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.detail || 'Email verification failed');
    }

    return data;
  }, []);

  // Initial auth check - relies on httpOnly cookie set by backend on prior login
  useEffect(() => {
    checkAuth(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Intentionally empty - only run on mount

  const refreshUser = useCallback(async () => {
    await checkAuth();
  }, [checkAuth]);

  return (
    <AuthContext.Provider value={{
      user,
      setUser,
      loading,
      token,
      login,
      register,
      continueAsGuest,
      processOAuthSession,
      logout,
      checkAuth,
      getAuthHeaders,
      requestPasswordReset,
      resetPassword,
      verifyEmail,
      refreshUser
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
